import axios from "axios";

const API_ORIGIN = (
  process.env.REACT_APP_ROHAN_API_ORIGIN ||
  "https://rohanbackend.axiomos.in"
).replace(/\/+$/, "");

const LATEST_KEY_ACCESS_TOKEN =
  process.env.REACT_APP_LATEST_KEY_ACCESS_TOKEN ||
  "t9aZI42xdKRz3q7C3ooEU3fmX8v";

const keyAxios = axios.create({
  timeout: 20000,
  headers: {
    Accept: "application/json",
  },
});

const rohanApiClient = axios.create({
  baseURL: API_ORIGIN,
  timeout: 30000,
  headers: {
    Accept: "application/json",
  },
});

let cachedKey = "";
let pendingKeyRequest = null;

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const readLatestKey = (payload) =>
  payload?.latestKey ||
  payload?.key ||
  payload?.apiKey ||
  payload?.data?.latestKey ||
  payload?.data?.key ||
  payload?.data?.apiKey ||
  "";

const isLatestKeyRequest = (pathname) =>
  String(pathname || "").startsWith("/latestKey");

const getOriginalUrl = (config) =>
  config.__rohanOriginalUrl || config.url || "";

const buildProtectedUrl = (rawUrl, key, baseURL) => {
  if (!rawUrl || !key) return rawUrl;

  const parsed = new URL(rawUrl, baseURL || `${API_ORIGIN}/`);
  const apiOrigin = new URL(API_ORIGIN);

  if (parsed.origin !== apiOrigin.origin) {
    return rawUrl;
  }

  if (isLatestKeyRequest(parsed.pathname)) {
    return rawUrl;
  }

  // Already protected. Leave it unchanged.
  if (/:[^/]+$/.test(parsed.pathname)) {
    return rawUrl;
  }

  const cleanPath = parsed.pathname.replace(/\/+$/, "") || "/";
  parsed.pathname = `${cleanPath}:${encodeURIComponent(key)}`;

  // IMPORTANT: key is added to pathname BEFORE parsed.search.
  if (isAbsoluteUrl(rawUrl)) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

const fetchLatestKey = async ({ force = false } = {}) => {
  if (!force && cachedKey) {
    return cachedKey;
  }

  if (!LATEST_KEY_ACCESS_TOKEN) {
    throw new Error(
      "Missing REACT_APP_LATEST_KEY_ACCESS_TOKEN in the frontend environment.",
    );
  }

  if (!pendingKeyRequest || force) {
    pendingKeyRequest = keyAxios
      .get(
        `${API_ORIGIN}/latestKey:${encodeURIComponent(
          LATEST_KEY_ACCESS_TOKEN,
        )}`,
      )
      .then((response) => {
        const latestKey = String(readLatestKey(response?.data) || "").trim();

        if (!latestKey) {
          throw new Error("Latest rotating API key was not returned.");
        }

        cachedKey = latestKey;
        return latestKey;
      })
      .finally(() => {
        pendingKeyRequest = null;
      });
  }

  return pendingKeyRequest;
};

rohanApiClient.interceptors.request.use(
  async (config) => {
    const originalUrl = getOriginalUrl(config);
    const parsed = new URL(originalUrl, config.baseURL || `${API_ORIGIN}/`);
    const apiOrigin = new URL(API_ORIGIN);

    if (
      parsed.origin !== apiOrigin.origin ||
      isLatestKeyRequest(parsed.pathname)
    ) {
      return config;
    }

    const latestKey = await fetchLatestKey();

    config.__rohanOriginalUrl = originalUrl;
    config.url = buildProtectedUrl(originalUrl, latestKey, config.baseURL);

    if (process.env.NODE_ENV !== "production") {
      console.debug("Protected Rohan API request:", {
        originalUrl,
        finalUrl: config.url,
        method: config.method,
      });
    }

    return config;
  },
  (error) => Promise.reject(error),
);

rohanApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;

    if (
      status !== 403 ||
      !config ||
      config.__rohanRetried ||
      !getOriginalUrl(config)
    ) {
      return Promise.reject(error);
    }

    try {
      config.__rohanRetried = true;
      cachedKey = "";

      const freshKey = await fetchLatestKey({ force: true });
      const originalUrl = getOriginalUrl(config);

      config.url = buildProtectedUrl(
        originalUrl,
        freshKey,
        config.baseURL,
      );

      return rohanApiClient.request(config);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export const invalidateRohanApiKey = () => {
  cachedKey = "";
};

export default rohanApiClient;
